// src/services/slackSync.js - Bi-directional Slack sync service
import { postSlackMessage, getIntegrationStatus, getIntegrationSettings, integrationsEnabled } from '../lib/integrations';

/**
 * Post an issue update to Slack
 * @param {Object} issue - The issue object from Firefighter
 * @param {string} action - The action performed (created, updated, resolved, etc.)
 * @param {Object} slackConfig - Slack settings with the channel to post to
 * @returns {Promise<Object>} Slack response with message timestamp
 */
export async function postIssueToSlack(issue, action = 'created', slackConfig) {
  if (!slackConfig?.channel) {
    console.warn('Slack channel not set, skipping sync');
    return null;
  }

  try {
    // If the issue originated from Slack, post as a thread reply
    const data = await postSlackMessage({
      channel: slackConfig.channel,
      text: formatIssueMessage(issue, action),
      thread_ts: issue.metadata?.slack_ts || null,
    });
    console.log('✅ Posted to Slack:', data);
    return data;
  } catch (error) {
    console.error('Error posting to Slack:', error);
    return null;
  }
}

/**
 * Format an issue for Slack message
 * @param {Object} issue - The issue object
 * @param {string} action - The action performed
 * @returns {string} Formatted Slack message
 */
function formatIssueMessage(issue, action) {
  const emoji = {
    created: '🆕',
    updated: '🔄',
    resolved: '✅',
    assigned: '👤',
    triaged: '📊'
  }[action] || '📝';

  const impactEmoji = {
    5: '🔴',
    4: '🟠',
    3: '🟡',
    2: '🟢',
    1: '⚪'
  }[issue.impact] || '⚪';

  let message = `${emoji} *Issue ${action.toUpperCase()}*\n\n`;
  message += `${impactEmoji} *${issue.title}*\n`;
  message += `• Status: ${issue.status}\n`;
  message += `• Tier: ${issue.tier}\n`;
  message += `• Impact: ${issue.impact}/5\n`;

  if (issue.owner) {
    message += `• Owner: ${issue.owner}\n`;
  }

  if (issue.source && issue.source !== 'Slack') {
    message += `• Source: ${issue.source}\n`;
  }

  if (issue.ext_key) {
    message += `• ID: ${issue.ext_key}\n`;
  }

  if (issue.summary && issue.summary !== issue.title) {
    message += `\n${issue.summary.substring(0, 200)}${issue.summary.length > 200 ? '...' : ''}`;
  }

  return message;
}

/**
 * Slack settings for bi-directional sync, or null when Slack isn't connected
 * or no channel is chosen.
 * @returns {Promise<Object|null>}
 */
export async function getSlackSyncConfig() {
  if (!integrationsEnabled) return null;
  try {
    const [status, settings] = await Promise.all([getIntegrationStatus(), getIntegrationSettings()]);
    return status.slack && settings.slack.channel ? settings.slack : null;
  } catch (error) {
    console.error('Error loading Slack sync config:', error);
    return null;
  }
}
